// SubirMedioModal.jsx
// Subida de un nuevo medio autónoma (no depende del editor del perfil).
// Dos pestañas:
//   - "Archivo SVG": sube un .svg directamente (sanitizado + base64 + POST /media/).
//   - "Desde Imagen": reutiliza el vectorizador (MediosInputImgEditor) en modo standalone.
import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { Upload, X, FileCode, Image as ImageIcon, ClipboardPaste } from 'lucide-react';
import { showErrorAlert } from '../../../../../components/HelperAlerts/HelpersAlerts.jsx';
import MediosInputImgEditor from '../../../components/Editor/Editores/MediosEditor/MediosInputImgEditor.jsx';

const sanitizeSVG = (svgString) =>
    DOMPurify.sanitize(svgString, { USE_PROFILES: { svg: true } });

// — Pestaña: subir archivo SVG —
const SubirSVGForm = ({ onClose, onCreated, initialFile }) => {
    const [file, setFile]       = useState(null);   // preview (objectURL)
    const [svg, setSVG]         = useState(null);   // svg sanitizado en base64
    const [nombre, setNombre]   = useState('');
    const [sharing, setSharing] = useState('privado');
    const [errors, setErrors]   = useState({});
    const [saving, setSaving]   = useState(false);

    useEffect(() => {
        return () => { if (file) URL.revokeObjectURL(file); };
    }, [file]);

    const loadSVGFile = useCallback((uploadedFile) => {
        if (!uploadedFile) return;

        if (uploadedFile.type !== 'image/svg+xml' && !uploadedFile.name?.toLowerCase().endsWith('.svg')) {
            showErrorAlert({
                title: 'Archivo no válido',
                text: 'Por favor, sube un archivo en formato SVG.',
                confirmButtonText: 'Entendido',
            });
            return;
        }

        const reader = new FileReader();
        reader.readAsDataURL(uploadedFile);
        reader.onload = () => {
            const rawSVG = reader.result.split(',')[1];
            const decodedSVG = atob(rawSVG);
            const sanitizedBase64SVG = btoa(sanitizeSVG(decodedSVG));
            setFile(URL.createObjectURL(uploadedFile));
            setSVG(sanitizedBase64SVG);
            // El backend exige nombre alfanumérico (isalnum): quitamos espacios,
            // guiones, etc. del nombre por defecto sacado del archivo.
            setNombre((currentName) => {
                const base = uploadedFile.name.split('.')[0].replace(/[^\p{L}\p{N}]/gu, '');
                return currentName || base;
            });
            setErrors((prev) => ({ ...prev, svg: undefined }));
        };
        reader.onerror = (error) => console.error('Error reading the file:', error);
    }, []);

    useEffect(() => {
        if (initialFile) loadSVGFile(initialFile);
    }, [initialFile, loadSVGFile]);

    const handleChangeSVG = (e) => {
        loadSVGFile(e.target.files[0]);
        e.target.value = '';
    };

    const validate = () => {
        const newErrors = {};
        if (!nombre.trim()) {
            newErrors.nombre = 'El nombre es obligatorio.';
        } else if (!/^[\p{L}\p{N}]+$/u.test(nombre)) {
            newErrors.nombre = 'Solo letras y números, sin espacios ni símbolos.';
        }
        if (!sharing) newErrors.sharing = 'Selecciona una opción.';
        if (!svg) newErrors.svg = 'Debes subir un archivo SVG.';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!validate() || saving) return;

        setSaving(true);
        try {
            // El backend (medios/views.py MediaList.post) lee request.data['medio'].
            const medio = { svg, nombre, sharing_permissions: sharing };
            const { data } = await axios.post('media/', { medio });
            onCreated?.(data);
            onClose?.();
        } catch (error) {
            console.error('Error creating medio:', error.response?.data?.error || error.message);
            showErrorAlert({
                text: error.response?.data?.error || 'Ocurrió un error al crear el medio.',
                confirmButtonText: 'Entendido',
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="flex flex-col md:flex-row gap-4 h-full">
            {/* Área de subida / preview */}
            <div className="md:w-2/5">
                <div className="relative h-48 md:h-full min-h-[12rem] rounded-lg glassCard">
                    <input
                        type="file"
                        onChange={handleChangeSVG}
                        accept=".svg"
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    {file ? (
                        <img src={file} alt="Preview SVG" className="w-full h-full object-contain p-3" />
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full text-secondary">
                            <Upload className="w-10 h-10 mb-2" />
                            <span>Seleccionar archivo SVG</span>
                        </div>
                    )}
                </div>
                {errors.svg && <p className="text-error text-sm mt-2">{errors.svg}</p>}
            </div>

            {/* Datos del medio */}
            <div className="flex-1 flex flex-col gap-4">
                <div className="form-control">
                    <label className="label">
                        <span className="label-text">Nombre del medio*</span>
                    </label>
                    <input
                        type="text"
                        value={nombre}
                        onChange={(e) => setNombre(e.target.value)}
                        className={`input input-bordered w-full ${errors.nombre ? 'input-error' : ''}`}
                        placeholder="Nombre del medio*"
                    />
                    {errors.nombre ? (
                        <span className="label-text-alt text-error mt-1">{errors.nombre}</span>
                    ) : (
                        <span className="label-text-alt text-base-content/50 mt-1">Solo letras y números, sin espacios.</span>
                    )}
                </div>

                <div className="form-control">
                    <label className="label">
                        <span className="label-text">Permisos</span>
                    </label>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                        <label className="cursor-pointer label flex items-center">
                            <input
                                type="radio"
                                name="sharing_permissions"
                                value="publico"
                                checked={sharing === 'publico'}
                                onChange={(e) => setSharing(e.target.value)}
                                className="radio radio-primary mr-2"
                            />
                            <span className="label-text">Compartir con la comunidad</span>
                        </label>
                        <label className="cursor-pointer label flex items-center">
                            <input
                                type="radio"
                                name="sharing_permissions"
                                value="privado"
                                checked={sharing === 'privado'}
                                onChange={(e) => setSharing(e.target.value)}
                                className="radio radio-primary mr-2"
                            />
                            <span className="label-text">Privado</span>
                        </label>
                    </div>
                    {errors.sharing && (
                        <span className="label-text-alt text-error mt-1">{errors.sharing}</span>
                    )}
                </div>

                <div className="mt-auto flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="btn btn-ghost">
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        disabled={!file || saving}
                        className={`btn btn-primary ${(!file || saving) ? 'btn-disabled' : ''}`}
                    >
                        {saving ? (
                            <><span className="loading loading-spinner loading-sm"></span> Subiendo…</>
                        ) : 'Crear medio'}
                    </button>
                </div>
            </div>
        </form>
    );
};

SubirSVGForm.propTypes = {
    onClose: PropTypes.func,
    onCreated: PropTypes.func,
    initialFile: PropTypes.instanceOf(File),
};

const TABS = [
    { id: 'svg',    label: 'Archivo SVG', Icon: FileCode },
    { id: 'imagen', label: 'Desde Imagen', Icon: ImageIcon },
];

const SubirMedioModal = ({ onClose, onCreated }) => {
    const [tab, setTab] = useState('svg');
    const [pastedInput, setPastedInput] = useState(null);

    const isURL = (value) => {
        try {
            const url = new URL(value);
            return url.protocol === 'http:' || url.protocol === 'https:';
        } catch {
            return false;
        }
    };

    const applyClipboardContent = ({ file, text }) => {
        if (file) {
            const isSvg = file.type === 'image/svg+xml' || file.name?.toLowerCase().endsWith('.svg');
            if (!isSvg && !file.type.startsWith('image/')) return false;

            setTab(isSvg ? 'svg' : 'imagen');
            setPastedInput({ type: 'file', value: file, key: Date.now() });
            return true;
        }

        const trimmed = text?.trim();
        if (!trimmed) return false;

        if (/^<svg[\s>]/i.test(trimmed)) {
            const svgFile = new File([trimmed], 'medio-pegado.svg', { type: 'image/svg+xml' });
            setTab('svg');
            setPastedInput({ type: 'file', value: svgFile, key: Date.now() });
            return true;
        }

        if (isURL(trimmed)) {
            setTab('imagen');
            setPastedInput({ type: 'url', value: trimmed, key: Date.now() });
            return true;
        }

        return false;
    };

    const handlePaste = (event) => {
        const items = Array.from(event.clipboardData?.items || []);
        const file = items.find(item => item.kind === 'file' && item.type.startsWith('image/'))?.getAsFile();
        const handled = applyClipboardContent({
            file,
            text: event.clipboardData?.getData('text/plain'),
        });

        if (handled) event.preventDefault();
    };

    const pasteFromClipboard = async () => {
        try {
            if (navigator.clipboard.read) {
                const clipboardItems = await navigator.clipboard.read();
                for (const item of clipboardItems) {
                    const imageType = item.types.find(type => type.startsWith('image/'));
                    if (imageType) {
                        const blob = await item.getType(imageType);
                        const extension = imageType === 'image/svg+xml' ? 'svg' : imageType.split('/')[1];
                        const file = new File([blob], `medio-pegado.${extension}`, { type: imageType });
                        if (applyClipboardContent({ file })) return;
                    }
                }
            }

            const text = await navigator.clipboard.readText();
            if (applyClipboardContent({ text })) return;

            showErrorAlert({ text: 'El portapapeles no contiene una imagen, un SVG o una URL válida.' });
        } catch (error) {
            console.error('Error reading clipboard:', error);
            showErrorAlert({ text: 'No se pudo leer el portapapeles. También puedes usar Ctrl+V dentro de esta ventana.' });
        }
    };

    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
            onClick={onClose}
            onPaste={handlePaste}
        >
            <div
                className="glassCard relative w-full max-w-5xl h-[85vh] p-5 flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-2 right-2 btn btn-ghost btn-sm btn-circle z-10"
                    aria-label="Cerrar"
                >
                    <X className="w-5 h-5" />
                </button>

                <div className="flex items-center justify-between gap-3 mb-3 pr-10">
                    <h3 className="text-lg font-semibold">Subir nuevo medio</h3>
                    <button type="button" onClick={pasteFromClipboard} className="btn btn-outline btn-sm gap-2">
                        <ClipboardPaste className="w-4 h-4" />
                        Pegar
                    </button>
                </div>

                {/* Pestañas */}
                <div role="tablist" className="tabs tabs-boxed shrink-0 mb-4 w-fit">
                    {TABS.map(({ id, label, Icon }) => (
                        <button
                            key={id}
                            type="button"
                            role="tab"
                            onClick={() => {
                                setTab(id);
                                setPastedInput(null);
                            }}
                            className={`tab gap-2 ${tab === id ? 'tab-active' : ''}`}
                        >
                            <Icon className="w-4 h-4" />
                            {label}
                        </button>
                    ))}
                </div>

                {/* Contenido de la pestaña */}
                <div className="flex-1 min-h-0 overflow-y-auto">
                    {tab === 'svg' ? (
                        <SubirSVGForm
                            key={pastedInput?.type === 'file' ? pastedInput.key : 'svg'}
                            onClose={onClose}
                            onCreated={onCreated}
                            initialFile={pastedInput?.type === 'file' ? pastedInput.value : null}
                        />
                    ) : (
                        <MediosInputImgEditor
                            key={pastedInput?.key || 'imagen'}
                            onClose={onClose}
                            onCreated={onCreated}
                            initialFile={pastedInput?.type === 'file' ? pastedInput.value : null}
                            initialUrl={pastedInput?.type === 'url' ? pastedInput.value : ''}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

SubirMedioModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    onCreated: PropTypes.func,
};

export default SubirMedioModal;
